import React, { useEffect, useRef } from 'react';
import { View, Animated, Easing, StyleSheet, Platform } from 'react-native';

export interface SpinnerProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    color?: string;
    secondaryColor?: string;
    className?: string; // Web escape hatch
}

const sizeValues: Record<string, number> = {
    sm: 16, // w-4
    md: 20, // w-5
    lg: 32, // w-8
    xl: 48, // w-12
};

/**
 * Universal Spinner Component
 * Precisely matches the existing dual-ring design:
 * - Outer: primary-600 (#16a34a)
 * - Inner: primary-400 (#4ade80)
 * - Counter-rotating animations
 */
export const Spinner: React.FC<SpinnerProps> = ({
    size = 'md',
    color = '#16a34a', // primary-600 default
    secondaryColor = '#4ade80', // primary-400 default
}) => {
    const spinValue = useRef(new Animated.Value(0)).current;
    const dimension = (sizeValues[size as keyof typeof sizeValues] || sizeValues.md) as number;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.timing(spinValue, {
                toValue: 1,
                duration: 800, // Slightly faster to match web 'animate-spin' energy
                easing: Easing.linear,
                useNativeDriver: Platform.OS !== 'web',
            })
        );
        animation.start();
        return () => animation.stop();
    }, [spinValue]);

    const spinForward = spinValue.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    const spinReverse = spinValue.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '-360deg'],
    });

    const ringThickness = Math.max(2, dimension * 0.1);

    return (
        <View
            style={[
                styles.container,
                { width: dimension, height: dimension }
            ]}
            accessibilityRole="progressbar"
            accessibilityLabel="Loading"
        >
            {/* Outer Ring - Matches border-t-primary-600 */}
            <Animated.View
                style={[
                    styles.ring,
                    {
                        width: dimension,
                        height: dimension,
                        borderRadius: dimension / 2,
                        borderWidth: ringThickness,
                        borderColor: 'transparent',
                        borderTopColor: color,
                        transform: [{ rotate: spinForward }],
                    },
                ]}
            />
            {/* Inner Ring - Matches border-t-primary-400 at 80% size */}
            <Animated.View
                style={[
                    styles.ring,
                    {
                        width: dimension * 0.8,
                        height: dimension * 0.8,
                        borderRadius: (dimension * 0.8) / 2,
                        borderWidth: ringThickness,
                        borderColor: 'transparent',
                        borderTopColor: secondaryColor,
                        top: dimension * 0.1,
                        left: dimension * 0.1,
                        transform: [{ rotate: spinReverse }],
                    },
                ]}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
    },
    ring: {
        position: 'absolute',
        borderStyle: 'solid',
    },
});
